/**
 * useWorkspaceBillingSnapshot — the signed-in pricing page's one question:
 * "what is THIS viewer's billing situation, in THIS workspace?"
 *
 * 2026-08-21 owner directive: /pricing must stop pitching "create your
 * account" to people who have one. Signed in, the right column becomes a
 * workspace panel — the tailored offer, the live plan with management, or
 * the same permission wall the subscription page shows.
 *
 * 2026-08-25 owner report ("the subscription and pricing pages need to load
 * much much faster — the user sees one thing and it takes several seconds for
 * them to see conflicting information"): the resolution itself moved to
 * `services/billingSnapshotCache`, shared with /subscription. That is what
 * makes the two pages agree — they now read ONE cached answer instead of
 * each re-deriving it — and what makes a repeat visit paint the real answer
 * in its FIRST frame instead of a guess it takes back seconds later. This
 * hook is now just the React binding over that cache.
 *
 * States:
 *  - loading   — genuinely unknown: no cached answer and the first resolution
 *                is still in flight. Callers MUST render a neutral skeleton
 *                here, never a guess — asserting an answer in this state is
 *                exactly the contradiction the owner reported.
 *  - offer     — no live wu_* plan and the viewer may buy
 *  - plan      — live wu_* plan the viewer may see; canManage gates the
 *                seat/term adjuster
 *  - view      — org member with billing.view only (read-only + requests)
 *  - wall      — org member with no billing permission at all
 */
import { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import type { HireCadence } from '../../config/workUnits';
import {
  type BillingSnapshot,
  fnUrl,
  idTokenFor,
  isBillingSnapshotFresh,
  readCachedBillingSnapshot,
  resolveBillingSnapshot,
  sameSnapshot,
  termFromPlan,
} from '../../services/billingSnapshotCache';

export interface WorkspaceBillingSnapshot {
  kind: 'loading' | BillingSnapshot['kind'];
  scope: 'personal' | 'org';
  /** Org target for subscribeDirect/changeSubscription; absent on personal. */
  accountId?: string;
  /** Human label for the workspace ("your workspace" on personal). */
  label: string;
  /** plan/view states with a subscription: */
  plan?: string;
  seats?: number;
  term?: HireCadence;
  currentPeriodEnd?: number | null;
  isTrial?: boolean;
  canManage?: boolean;
}

export { fnUrl, termFromPlan };
export type { BillingSnapshot };

const LOADING: WorkspaceBillingSnapshot = {
  kind: 'loading', scope: 'personal', label: 'your workspace',
};

/**
 * The public answer. Signed out there is no workspace to resolve, so this is
 * the TRUTH, not a placeholder — and it must be the state from the first
 * frame: the card's CTA is disabled while `kind` is 'loading', so leaving a
 * logged-out visitor in that state left the pricing page's primary button
 * dead, reading "checking your workspace…" forever (the hook used to return
 * early when disabled and never leave the loading state).
 */
const SIGNED_OUT: WorkspaceBillingSnapshot = {
  kind: 'offer', scope: 'personal', label: 'your workspace',
};

/** Same wait-for-auth contract the page has always used (AUTH_WAIT_MS). */
export async function getAuthToken(): Promise<string | null> {
  return idTokenFor(getAuth());
}

/**
 * The signed-in uid, synchronously. The caller's own id (AuthContext restores
 * it from storage in the first frame) is preferred over firebase's, which
 * restores its session asynchronously and is often still null at mount — the
 * difference between painting the real answer in frame 1 and painting a
 * skeleton first.
 */
function uidNow(preferred?: string | null): string | null {
  const clean = String(preferred ?? '').trim();
  if (clean) return clean;
  try { return getAuth().currentUser?.uid ?? null; } catch { return null; }
}

/** The cached answer for the signed-in viewer, or the loading state. */
function seed(enabled: boolean, preferredUid?: string | null): WorkspaceBillingSnapshot {
  if (!enabled) return SIGNED_OUT;
  try {
    return readCachedBillingSnapshot(uidNow(preferredUid)) ?? LOADING;
  } catch {
    return LOADING;
  }
}

export function useWorkspaceBillingSnapshot(
  enabled: boolean,
  preferredUid?: string | null,
): WorkspaceBillingSnapshot {
  // Seeded synchronously: on a repeat visit the panel renders the REAL answer
  // in the first frame, so there is nothing to contradict.
  const [snap, setSnap] = useState<WorkspaceBillingSnapshot>(() => seed(enabled, preferredUid));

  useEffect(() => {
    let canceled = false;
    if (!enabled) {
      setSnap(SIGNED_OUT);
      return undefined;
    }

    const uid = uidNow(preferredUid);
    if (!uid) {
      // Signed out: there is no workspace to resolve, and the offer IS the
      // correct answer rather than a guess.
      setSnap(SIGNED_OUT);
      return undefined;
    }

    const cached = readCachedBillingSnapshot(uid);
    if (cached) setSnap(cached);
    // A fresh memory copy is authoritative — skip the network entirely.
    if (cached && isBillingSnapshotFresh(uid)) return undefined;

    void resolveBillingSnapshot(uid).then((resolved) => {
      if (canceled) return;
      if (!resolved) {
        // Unknowable right now. Keep a cached answer if we have one rather
        // than flipping to a guess; with nothing cached, the offer is the
        // page's historical fallback.
        if (!cached) setSnap(SIGNED_OUT);
        return;
      }
      // Only re-render when the facts actually changed — a background refresh
      // that confirms the painted answer must be invisible.
      setSnap((prev) => (
        prev.kind !== 'loading' && sameSnapshot(prev as BillingSnapshot, resolved)
          ? prev
          : resolved
      ));
    });

    return () => { canceled = true; };
  }, [enabled, preferredUid]);

  return snap;
}
